import { Card, H4 } from "@blueprintjs/core";
import { segmentTrackClickUpgradeInTopUpgradesChart } from "in_queue/analytics";
import { useScenarioResults } from "in_queue/contexts/ScenarioResultsContext";
import { ScenarioAllocatedUpgrade } from "in_queue/types/scenarioResultType";
import { formatCosts, formatDollarsPerMw } from "in_queue/utils/formatting";
import { aggregateUpgradesByContribution } from "in_queue/utils/upgradeContributions";
import { useState } from "react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";
import {
    NEIGHBOR_PALETTE,
    OTHER_NEIGHBOR_COLOR,
    YOU_COLOR
} from "./chartColors";
import { ChartPlaceholder, EmptyChartNotice } from "./chartHelpers";
import css from "./charts.module.scss";
import {
    ProjectRankStats,
    UpgradeBreakdownTable
} from "./UpgradeBreakdownTable";

/** Top-N upgrades to surface in the chart, by `myCost` descending. */
const TOP_N = 5;
/** Max neighbor projects per upgrade that get their own bar color. */
const PROJECT_MAX = 6;

interface Props {
    upgrades: ScenarioAllocatedUpgrade[];
    currentProjectId: string;
    /** From ScenarioResultsContext — second-batch (related projects) fetched. */
    isFullyLoaded: boolean;
}

/**
 * "Top Upgrades" — horizontal stacked bar showing the network upgrades
 * where the current project has the largest allocated cost. Each row is
 * one upgrade; bar width = the upgrade's total allocated cost; segments
 * are colored per contributing project, with the current project pinned
 * to nira-blue and a stable palette assigned to neighbors across rows.
 *
 * Clicking a row opens an inline `UpgradeBreakdownTable` with full
 * contributor detail.
 *
 * Per-project rank / $/MW metadata used for the legend + breakdown table
 * rides on `ScenarioResultsContext.projects`, which already includes the
 * full per-scenario pool regardless of subscription.
 */
export const SharedUpgradesChart: React.FC<Props> = ({
    upgrades,
    currentProjectId,
    isFullyLoaded
}) => {
    // Track which upgrade row is expanded (click → inline breakdown table).
    // Declared at the top of the component (before any early returns) so the
    // hook count stays stable across the loading -> loaded transition.
    const [expandedId, setExpandedId] = useState<string | undefined>(undefined);

    const { scenarioResults } = useScenarioResults();

    // Bail out until the second-batch fetch (other projects' allocations)
    // has finished so the stacked bars don't flicker as data streams in.
    if (!isFullyLoaded || scenarioResults === "loading") {
        return (
            <ChartPlaceholder
                title="Top Upgrades"
                message="Loading allocations for related projects…"
            />
        );
    }

    // The legend and the breakdown table annotate each peer with its
    // rank and $/MW, both of which ride on the rows the results query
    // already returned.
    const projectStats = new Map<string, ProjectRankStats>();
    for (const project of scenarioResults.projects) {
        if (
            project.rank === undefined ||
            project.rankPercentile === undefined ||
            project.totalRanked === undefined
        ) {
            continue;
        }
        projectStats.set(project.projectId, {
            rank: project.rank,
            total: project.totalRanked,
            percentile: project.rankPercentile,
            costPerMw: project.costPerMw
        });
    }

    // Group upgrades by ID, compute per-project contributions.
    const allUpgradeRows = aggregateUpgradesByContribution(
        upgrades,
        currentProjectId
    );

    // Top-N upgrades by current-project allocation.
    const top = allUpgradeRows
        .filter((row) => row.myCost > 0)
        .sort((a, b) => b.myCost - a.myCost)
        .slice(0, TOP_N);

    if (top.length === 0) {
        return (
            <EmptyChartNotice
                title="Top Upgrades"
                message="This project has no allocated upgrades in this scenario."
            />
        );
    }

    // Sticky color palette per neighbor project across the top-N rows.
    // The current project is pinned to nira-blue; long tail collapses
    // to a grey "Other" bucket per row.
    const projectColor = new Map<string, string>();
    projectColor.set(currentProjectId, YOU_COLOR);
    const seenColors = new Set<string>([currentProjectId]);
    for (const row of top) {
        const others = row.contributions
            .filter((c) => c.projectId !== currentProjectId)
            .slice(0, PROJECT_MAX);
        for (const c of others) {
            if (seenColors.has(c.projectId)) continue;
            const color =
                NEIGHBOR_PALETTE[
                    (seenColors.size - 1) % NEIGHBOR_PALETTE.length
                ];
            projectColor.set(c.projectId, color);
            seenColors.add(c.projectId);
        }
    }

    // Build chart rows (Recharts horizontal stacked bar).
    type ChartRow = Record<string, number | string>;
    const chartRows: ChartRow[] = top.map((row) => {
        const obj: ChartRow = {
            upgradeId: row.upgradeId,
            label: row.name,
            __total: row.totalCost
        };
        let other = 0;
        for (const c of row.contributions) {
            if (projectColor.has(c.projectId)) {
                obj[c.projectId] = c.cost;
            } else {
                other += c.cost;
            }
        }
        if (other > 0) obj["__other"] = other;
        return obj;
    });

    const projectKeys = Array.from(projectColor.keys());

    const expandedRow = expandedId
        ? allUpgradeRows.find((r) => r.upgradeId === expandedId)
        : undefined;

    // Shared click handler for the upgrade bars + the "Other projects"
    // bar. Tracks the click and toggles the inline breakdown table.
    // Recharts hands the bar's data payload (a ChartRow) to onClick but
    // types it loosely, so we narrow with a runtime predicate before
    // reading the `upgradeId` field.
    const hasUpgradeId = (e: unknown): e is { readonly upgradeId: string } =>
        typeof e === "object" &&
        e !== null &&
        "upgradeId" in e &&
        typeof (e as { upgradeId: unknown }).upgradeId === "string";

    const handleUpgradeBarClick = (entry: unknown) => {
        if (!hasUpgradeId(entry)) return;
        const upgradeId = entry.upgradeId;
        const isExpanding = expandedId !== upgradeId;
        segmentTrackClickUpgradeInTopUpgradesChart({
            upgradeId,
            expanded: isExpanding
        });
        setExpandedId(isExpanding ? upgradeId : undefined);
    };

    return (
        <Card className={css.chartCard}>
            <div className={css.chartHeader}>
                <H4 className={css.chartTitle}>Top Upgrades</H4>
            </div>
            <ResponsiveContainer
                width="100%"
                height={Math.max(220, top.length * 56 + 60)}
            >
                <BarChart
                    data={chartRows}
                    layout="vertical"
                    margin={{ top: 10, right: 30, bottom: 30, left: 80 }}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis
                        type="number"
                        tick={{ fontSize: 11 }}
                        tickFormatter={formatCosts}
                        label={{
                            value: "Total allocated $ on this upgrade",
                            position: "insideBottom",
                            offset: -10,
                            style: { fontSize: 11 }
                        }}
                    />
                    <YAxis
                        type="category"
                        dataKey="label"
                        tick={{ fontSize: 11 }}
                        width={220}
                        interval={0}
                    />
                    <Tooltip
                        isAnimationActive={false}
                        content={({ payload }) => {
                            if (!payload?.length) return null;
                            const row = payload[0].payload as ChartRow;
                            const total = Number(row.__total ?? 0);
                            return (
                                <div className={css.tooltip}>
                                    <div>
                                        <b>{row.label}</b>
                                    </div>
                                    <div className={css.tooltipSubtle}>
                                        Total: {formatCosts(total)} · click to
                                        see full % breakdown
                                    </div>
                                    {payload
                                        .filter((p) => Number(p.value) > 0)
                                        .map((p) => {
                                            const key = p.dataKey as string;
                                            const value = Number(p.value);
                                            const pct =
                                                total > 0
                                                    ? (value / total) * 100
                                                    : 0;
                                            const label =
                                                key === "__other"
                                                    ? "Other projects"
                                                    : key === currentProjectId
                                                    ? `${key} (this project)`
                                                    : key;
                                            return (
                                                <div key={key}>
                                                    <span
                                                        className={
                                                            css.legendDot
                                                        }
                                                        style={{
                                                            background: p.color
                                                        }}
                                                    />
                                                    {label}:{" "}
                                                    {formatCosts(value)} (
                                                    {pct.toFixed(1)}%)
                                                </div>
                                            );
                                        })}
                                </div>
                            );
                        }}
                    />
                    {projectKeys.map((pid) => (
                        <Bar
                            key={pid}
                            dataKey={pid}
                            stackId="contributions"
                            fill={projectColor.get(pid) ?? OTHER_NEIGHBOR_COLOR}
                            cursor="pointer"
                            isAnimationActive={false}
                            onClick={handleUpgradeBarClick}
                        />
                    ))}
                    <Bar
                        dataKey="__other"
                        stackId="contributions"
                        fill={OTHER_NEIGHBOR_COLOR}
                        cursor="pointer"
                        isAnimationActive={false}
                        onClick={handleUpgradeBarClick}
                    />
                </BarChart>
            </ResponsiveContainer>
            <div className={css.upgradeLegend}>
                {projectKeys.map((pid) => {
                    const stats = projectStats.get(pid);
                    const isYou = pid === currentProjectId;
                    return (
                        <div key={pid} className={css.legendRow}>
                            <span
                                className={css.legendDot}
                                style={{ background: projectColor.get(pid) }}
                            />
                            <span className={css.legendTitle}>
                                {pid}
                                {isYou && " (this project)"}
                                {stats && (
                                    <span className={css.tooltipSubtle}>
                                        {" "}
                                        · P{stats.percentile}
                                        {stats.costPerMw !== undefined &&
                                            ` · ${formatDollarsPerMw(
                                                stats.costPerMw
                                            )}`}
                                    </span>
                                )}
                            </span>
                        </div>
                    );
                })}
                <div className={css.legendRow}>
                    <span
                        className={css.legendDot}
                        style={{ background: OTHER_NEIGHBOR_COLOR }}
                    />
                    <span className={css.legendTitle}>Other projects</span>
                </div>
            </div>
            {expandedRow && (
                <UpgradeBreakdownTable
                    upgrade={expandedRow}
                    currentProjectId={currentProjectId}
                    projectStats={projectStats}
                    onClose={() => setExpandedId(undefined)}
                />
            )}
        </Card>
    );
};
